<template>
  <v-form ref="DEMANDE_ATTESTATION" @submit.prevent="sectionSave" lazy-validation class="section">
    <div>
      <section-header v-if="!hideTitle"
                      :ressource="ressource"
                      :isExtended="extended"
                      :dialog="dialog"
                      :picto="picto()"
                      :fiche=fiche
                      :isAbstract=isAbstract
                      @extended=setExtended
                      @closeDialog=closeDialog
                      ref="DEMANDE_ATTESTATION">
      </section-header>
      <div v-if="extended" id="field-container" :class="dialog ? 'p-0 p-md-3' : 'py-2'">
        <etablissement-select v-if="isDisplayed('ETABLISSEMENT_ID')"
                              ref="ETABLISSEMENT_ID"
                              :theme="theme"
                              :showMissing="showMissing"
                              v-model:value=etablissementId
                              :moduleId=moduleId
                              libelle="'Etablissement'"
                              newLabel=''
                              selectText=""
                              @status=statusEtablissementId
                              :requiredAttributs="requiredAttributs"
                              :autoSelect=true
                              :ressource=ressourceEtablissementSelect
                              :filters=etablissementIdFilter
                              :toggleReset=resetEtablissementId
                              :required="isRequired('ETABLISSEMENT_ID')"
                              :contextId=attestation.id
                              :readOnly="readOnly('ETABLISSEMENT_ID')"
                              :objType=objType
                              :fiche=fiche
                              :sectionName="sectionName">
        </etablissement-select>
        <attestation-def-select v-if="isDisplayed('ATTESTATION_DEF_ID')"
                                ref="ATTESTATION_DEF_ID"
                                :theme="theme"
                                :showMissing="showMissing"
                                v-model:value=attestationDefId
                                :moduleId=moduleId
                                libelle="'Organisme'"
                                newLabel=''
                                selectText=""
                                @status=statusAttestationDefId
                                :requiredAttributs="requiredAttributs"
                                :autoSelect=true
                                :ressource=ressourceAttestationDefSelect
                                :filters=attestationDefIdFilter
                                :toggleReset=resetAttestationDefId
                                :required="isRequired('ATTESTATION_DEF_ID')"
                                :contextId=attestation.id
                                :readOnly="readOnly('ATTESTATION_DEF_ID')"
                                :objType=objType
                                :fiche=fiche
                                :sectionName="sectionName">
        </attestation-def-select>
        <attestation-date-demande v-if="isDisplayed('DATE_DEMANDE')"
                                  ref="DATE_DEMANDE"
                                  :theme="theme"
                                  :showMissing="showMissing"
                                  v-model:value=dateDemande
                                  @status=statusDateDemande
                                  :periods='periodDateDemande'
                                  :required="isRequired('DATE_DEMANDE')"
                                  :contextId=attestation.id
                                  :readOnly="readOnly('DATE_DEMANDE')"
                                  :objType=objType
                                  :fiche=fiche
                                  :sectionName="sectionName">
        </attestation-date-demande>
        <attestation-statut v-if="isDisplayed('STATUT')"
                            ref="STATUT"
                            :theme="theme"
                            :showMissing="showMissing"
                            v-model:value=statut
                            @status=statusStatut
                            :required="isRequired('STATUT')"
                            :contextId=attestation.id
                            :readOnly="readOnly('STATUT')"
                            :objType=objType
                            :fiche=fiche
                            :sectionName="sectionName">
        </attestation-statut>
        <attestation-date-valid v-if="isDisplayed('DATE_VALID')"
                                ref="DATE_VALID"
                                :theme="theme"
                                :showMissing="showMissing"
                                v-model:value=dateValid
                                @status=statusDateValid
                                :periods='periodDateValid'
                                :required="isRequired('DATE_VALID')"
                                :contextId=attestation.id
                                :readOnly="readOnly('DATE_VALID')"
                                :objType=objType
                                :fiche=fiche
                                :sectionName="sectionName">
        </attestation-date-valid>
        <attestation-motif v-if="isDisplayed('MOTIF')"
                           ref="MOTIF"
                           :theme="theme"
                           :showMissing="showMissing"
                           v-model:value=motif
                           @status=statusMotif
                           :required="isRequired('MOTIF')"
                           :contextId=attestation.id
                           :readOnly="readOnly('MOTIF')"
                           :objType=objType
                           :fiche=fiche
                           :sectionName="sectionName">
        </attestation-motif>

        <div>
          <v-file-input v-if="isDisplayed('FILE')"
                        v-model="file"
                        :error="fileMissing && file == null"
                        :disabled="progress"
                        :accept="acceptedFiles"
                        :label="fileInputLabel">
          </v-file-input>
        </div>
      </div>
    </div>
    <div v-if="extended" class="d-flex flex-row-reverse">
      <loader-button v-if="saveButton"
                     class="ms-2"
                     type="submit"
                     :loading="saveLoading"
                     :label="saveLabel"
                     :disabled="!updatedAndValid">
      </loader-button>
      <div v-for="(button, index) in extraButtons" :key="index">
        <loader-button class="ms-2"
                       :outlined="button.outlined"
                       @callback="button.callback()"
                       :type="button.type"
                       :label="button.label"
                       :loading="button.loading"
                       :disabled="button.disabled">
        </loader-button>
      </div>
    </div>
    <div class="d-flex flex-row-reverse" v-if="saveFailed && saveButton && extended">
      <p class="text-12 text-bold error-text">{{errorTxt}}</p>
    </div>
  </v-form>
</template>

<script lang="ts">
  import { defineComponent } from "vue";
  import SectionDemandeAttestation from '@/generated/sections/SectionDemandeAttestation.vue';
  import Button from '@/framework/base/classes/Button';
  import Attestation from '@/extend/classes/Attestation';
  import { AttestationStatuts } from "../../generated/classes/Attestation";
  import { storeAttestation, storeDocument, storeSession } from '@/generated/stores';
  import UserProfile, { EnumProfile } from '@/framework/base/classes/UserProfile';
  import SelectField from "@/framework/base/components/SelectField.vue";
  import Document from "@/generated/classes/Document.ts";
  import { mimeTypes } from "@/framework/files/helpers/FileTypes.ts";
  import Files from "@/generated/classes/Files.ts";
  import { ObjTypes } from "@/generated/classes/ObjTypes.ts";

  export default defineComponent({
    extends: SectionDemandeAttestation,
    components: { SelectField },
    data() {
      const file: File | null = null;
      return {
        fileInputLabel: 'Choisir un fichier',
        progressLabel: "Changement en cours",
        progress: false,
        file,
        loading: false,
        fileMissing: false
      };
    },
    mounted(): void {
      storeAttestation.registerAttestation(null);
    },
    computed: {
      acceptedFiles(): string {
        let res = 'pdf'
        mimeTypes.forEach(m => {
          if (m.type == 'pdf') {
            res = m.mime;
          }
        })
        return res;
      },
      isAdmin(): boolean {
        const user = storeSession.connectedUser;
        if (user) {
          return UserProfile.hasProfile(user, EnumProfile.admin);
        }
        return false;
      },
      saveButton(): boolean {
        return this.isAdmin;
      },
      extraButtons(): Button[] {
        const buttons: Button[] = [];
        {
          const button = new Button();
          button.label = this.attestation.documentId > 0 ? 'Renouveller la demande' : 'Faire la demande';
          button.show = true;
          button.type = "button"
          button.disabled = !this.attestation.canRenew;
          button.outlined = true;
          button.loading = false;
          button.callback = () => this.renouveller(button);
          buttons.push(button);
        }
        {
          const button = new Button();
          button.label = "Voir l'attestation";
          button.show = true;
          button.type = "button"
          button.disabled = this.attestation.documentId <= 0;
          button.outlined = true;
          button.loading = false;
          button.callback = () => this.showDocument(button);
          buttons.push(button);
        }
        return buttons;
      }
    },
    methods: {
      readOnly(value: string): boolean {
        let res = false;
        if (!this.attestation.canUpdate) {
          return true;
        }
        if (this.readOnlyMap.size == 0) {
          this.readOnlyMap.set("ETABLISSEMENT_ID", true);
          this.readOnlyMap.set("ATTESTATION_DEF_ID", true);
          this.readOnlyMap.set("STATUT", !this.isAdmin);
          this.readOnlyMap.set("DATE_DEMANDE", true);
          this.readOnlyMap.set("DATE_VALID", !this.isAdmin);
          this.readOnlyMap.set("MOTIF", !this.isAdmin);
        }
        const readOnly = this.readOnlyMap.get(value);
        if (readOnly != undefined) {
          res = readOnly;
        }
        return res;
      },
      isDisplayed(value: string): boolean {
        let res = true;
        if (this.displayMap.size == 0) {
          const requiredAttributs = this.getRequiredAttributs();
          this.displayMap.set("ETABLISSEMENT_ID", this.isAdmin);
          this.displayMap.set("ATTESTATION_DEF_ID", true);
          this.displayMap.set("DATE_DEMANDE", this.attestation.status != AttestationStatuts.NON_DEMANDE);
          this.displayMap.set("STATUT", true);
          this.displayMap.set("DATE_VALID", true);
          this.displayMap.set("FILE", this.isAdmin);
        }
        this.displayMap.set("MOTIF", this.attestation.status == AttestationStatuts.REFUSE);
        const display = this.displayMap.get(value);
        if (display != undefined) {
          res = display;
        }
        return res;
      },
      isRequired(value: string): boolean {
        let res = true;
        const requiredAttributs = this.getRequiredAttributs();
        if (this.requiredMap.size == 0) {
          this.requiredMap.set("ETABLISSEMENT_ID", true);
          this.requiredMap.set("ATTESTATION_DEF_ID", true);
          this.requiredMap.set("DATE_DEMANDE", true);
          this.requiredMap.set("STATUT", true);
          this.requiredMap.set("MOTIF", true);
        }
        this.requiredMap.set("DATE_VALID", this.attestation.statut == AttestationStatuts.OBTENU);
        const required = this.requiredMap.get(value);
        if (required != undefined) {
          res = required;
        }
        return res;
      },
      fieldUpdated(field: string, value: unknown): void {
        if (field == "STATUT") {
          const required = value == AttestationStatuts.OBTENU;
          this.requiredMap.set("DATE_VALID", required);
        }
      },
      renouveller(button: Button): Promise<void> {
        button.loading = true;
        return new Promise((resolve) => {
          Attestation.renew(this.attestation).then((res) => {
            button.loading = false;
          })
          resolve();
        });
      },
      showDocument(button: Button): Promise<void> {
        button.loading = true;
        return new Promise((resolve) => {
          Attestation.display(this.attestation).then((res) => {
            button.loading = false;
          })
          resolve();
        });
      },
      submitFile(): Promise<void> {
        this.loading = true;
        if (this.file != null) {
          this.progress = true;
          const formData = new FormData()
          formData.append('file', this.file);
          formData.append('service', 'Attestation');
          formData.append('objId', this.attestation.id.toString());
          return new Promise((resolve) => {
            Files.upload(formData).then(() => {
              this.loading = false;
              resolve();
            });
          });
        } else {
          return new Promise((resolve) => {
            resolve();
          });
        }
      },
      sectionSave(): void {
        if (this.attestation.statut == AttestationStatuts.OBTENU) {
          if (this.file != null && this.checkFields()) {
            this.submitFile().then(() => {
              this.save();
            })
          } else {
            this.fileMissing = true;
          }
        } else {
          this.save();
        }
      }
    }


  })
</script>
